/**
 * Signup draft state: the non-secret fields the wizard collects, mirrored into
 * sessionStorage so a refresh mid-signup resumes where the user was, plus the
 * debounced team-code availability probe.
 *
 * Passwords are deliberately excluded — they live in component state only and a
 * refresh sends the user back to the account step.
 */
import { useEffect, useRef, useState } from 'react';

import type { ApiClient } from '../../lib/api-client';
import { STORAGE_KEYS } from '../../lib/constants';
import { readSessionValue, writeSessionValue } from '../../lib/session-storage';

import { slugify, validateSlug, type SlugCheckState } from './signup-validation';

const SLUG_DEBOUNCE_MS = 400;

export interface SignupDraft {
  teamName: string;
  slug: string;
  email: string;
  displayName: string;
  /** Once the user edits the code by hand it stops tracking the team name. */
  isSlugEdited: boolean;
}

const EMPTY_DRAFT: SignupDraft = {
  teamName: '',
  slug: '',
  email: '',
  displayName: '',
  isSlugEdited: false,
};

function loadDraft(): SignupDraft {
  const raw = readSessionValue(STORAGE_KEYS.signupDraft);
  if (raw === null) return EMPTY_DRAFT;
  try {
    return { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<SignupDraft>) };
  } catch {
    return EMPTY_DRAFT;
  }
}

export interface SignupDraftController {
  draft: SignupDraft;
  patchDraft: (patch: Partial<SignupDraft>) => void;
  clearDraft: () => void;
}

export function useSignupDraft(): SignupDraftController {
  const [draft, setDraft] = useState<SignupDraft>(loadDraft);

  useEffect(() => {
    writeSessionValue(STORAGE_KEYS.signupDraft, JSON.stringify(draft));
  }, [draft]);

  function patchDraft(patch: Partial<SignupDraft>): void {
    setDraft((current) => {
      const next = { ...current, ...patch };
      if (patch.slug !== undefined) next.isSlugEdited = true;
      if (patch.teamName !== undefined && !next.isSlugEdited) next.slug = slugify(patch.teamName);
      return next;
    });
  }

  function clearDraft(): void {
    writeSessionValue(STORAGE_KEYS.signupDraft, null);
  }

  return { draft, patchDraft, clearDraft };
}

/** Debounced `GET /signup/v1/slug-availability` for the team-code field. */
export function useSlugAvailability(apiClient: ApiClient, slug: string): SlugCheckState {
  const [state, setState] = useState<SlugCheckState>({ status: 'idle' });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (validateSlug(slug) !== null) {
      setState({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setState({ status: 'checking', slug });
    timerRef.current = setTimeout(() => {
      apiClient
        .checkSlugAvailability(slug, controller.signal)
        .then((availability) =>
          setState({ status: availability.is_available ? 'available' : 'unavailable', slug })
        )
        .catch(() => setState({ status: 'error', slug }));
    }, SLUG_DEBOUNCE_MS);
    return () => {
      controller.abort();
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [apiClient, slug]);

  return state;
}
